import React, { useEffect, useRef, useState } from 'react';
import { STATS } from '../data';
import SectionHead from './SectionHead';
import { useLang } from '../LangContext';

function useCountUp(target, active, duration = 1400) {
  const [count, setCount] = useState(0);
  const done = useRef(false);

  useEffect(() => {
    if (!active || done.current) return;
    const end = parseInt(target, 10);
    if (isNaN(end)) return;
    done.current = true;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCount(Math.round(ease * end));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, target, duration]);

  return count;
}

function StatItem({ stat, active, lang }) {
  const count = useCountUp(stat.num, active);
  const isNumeric = !isNaN(parseInt(stat.num, 10));

  return (
    <div className="stat tgt">
      <div className="stat__num">
        {isNumeric ? count : stat.num}
        {stat.small && <span className="small">{stat.small}</span>}
      </div>
      <div className="stat__label">{stat.label[lang]}</div>
    </div>
  );
}

export default function Stats() {
  const { lang, t } = useLang();
  const sectionRef = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="s fullpage-section" id="stats" ref={sectionRef}>
      <div className="s__inner">
        <SectionHead label={t.stats.label} title={t.stats.title} />
        <div className="stats">
          {STATS.map((s, i) => (
            <StatItem key={i} stat={s} active={active} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
}
